// src/pages/Account/MyReservationsPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './MyReservationsPage.module.css';
import Calendar from '../../components/Calendar/Calendar';
import ReservationForm from '../../components/ReservationForm/ReservationForm';
import { useAuth } from '../../contexts/AuthContext';
import { getCalendarReservations, cancelReservation, getMyReservations, updateReservationDetails, getSigningUrl } from '../../services/reservationService';
import { getClosures, getFacilityInfo } from '../../services/facilityService';
import { getCalendarBlocks } from '../../services/calendarBlockService';
import Button from '../../components/Button/Button';
import FormInput from '../../components/FormInput/FormInput';
import ReservationProgressBar from '../../components/ReservationProgressBar/ReservationProgressBar';

const generateDateRange = (startDateStr, endDateStr) => {
    const dates = [];
    const start = new Date(startDateStr + 'T00:00:00Z');
    const end = new Date(endDateStr + 'T00:00:00Z');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return dates;
    let current = new Date(start);
    while (current <= end) {
        const year = current.getUTCFullYear();
        const month = String(current.getUTCMonth() + 1).padStart(2, '0');
        const day = String(current.getUTCDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setUTCDate(current.getUTCDate() + 1);
    }
    return dates;
};

const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

// Determine the next required action for a reservation
const getNextAction = (reservation) => {
    // Only show actions for pending reservations
    if (reservation.status !== 'pending') {
        if (reservation.status === 'approved') return 'complete';
        return 'none';
    }
    if (reservation.agreementStatus !== 'signed' && reservation.agreementStatus !== 'signed_manual') return 'sign_agreement';
    if (reservation.paymentStatus !== 'paid' && reservation.paymentStatus !== 'paid_manual') return 'make_payment';
    return 'complete';
};

const USER_RESERVATION_STATUS_TABS = [
    { label: 'All Active', status: 'all' },
    { label: 'Pending', status: 'pending' },
    { label: 'Approved', status: 'approved' },
    { label: 'Inactive', status: 'inactive' },
];

// Returns a human-readable label based on status + cancellationReason
const getStatusLabel = (res) => {
    if (res.status === 'cancelled') {
        if (res.cancellationReason === 'expired_unpaid') return 'Expired';
        return 'Cancelled';
    }
    if (res.status === 'rejected') return 'Declined';
    return res.status.replace('_', ' ');
};

// CSS class for status badge (maps to existing module styles)
const getStatusClass = (res) => {
    if (res.status === 'rejected') return 'declined';
    return res.status;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const MyReservationsPage = () => {
    const CANCELLABLE_STATUSES = ['pending', 'approved'];
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [selectedDates, setSelectedDates] = useState([]);
    const [calendarReservations, setCalendarReservations] = useState([]);
    const [closures, setClosures] = useState([]);
    const [reservationError, setReservationError] = useState(null);
    const [isLoadingSigningUrl, setIsLoadingSigningUrl] = useState(null); // ID of reservation loading url
    const [blocks, setBlocks] = useState([]);
    const [facility, setFacility] = useState(null);
    const [isLoadingReservations, setIsLoadingReservations] = useState(true);
    const [viewingReservation, setViewingReservation] = useState(null);
    const [calendarDisplayDate, setCalendarDisplayDate] = useState(new Date());

    const [tableReservations, setTableReservations] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [tableError, setTableError] = useState(null);
    const [activeFilterStatus, setActiveFilterStatus] = useState('all');

    const [expandedReservationId, setExpandedReservationId] = useState(null);
    const [editingReservationId, setEditingReservationId] = useState(null);
    const [editableEventName, setEditableEventName] = useState('');
    const [editableEventDescription, setEditableEventDescription] = useState('');
    const [editableAdditionalInfo, setEditableAdditionalInfo] = useState('');

    const [editableGuestName, setEditableGuestName] = useState('');
    const [editableGuestEmail, setEditableGuestEmail] = useState('');
    const [editableGuestPhone, setEditableGuestPhone] = useState('');
    const [editableGroupDescription, setEditableGroupDescription] = useState('');
    const [editablePurposeOfVisit, setEditablePurposeOfVisit] = useState('');
    const [editableAdultCount, setEditableAdultCount] = useState(0);
    const [editableChildCount, setEditableChildCount] = useState(0);
    const [editableResponsiblePerson, setEditableResponsiblePerson] = useState('');

    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [editError, setEditError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!currentUser?.id) return;
        setIsLoadingReservations(true);
        setReservationError(null);
        const year = calendarDisplayDate.getFullYear();
        const month = calendarDisplayDate.getMonth() + 1;

        try {
            const startOfMonth = new Date(Date.UTC(year, month - 1, 1));
            const endOfMonth = new Date(Date.UTC(year, month, 1));

            const [resData, closureData, facilityData, blocksData] = await Promise.all([
                getCalendarReservations(year, month),
                getClosures({ start: startOfMonth.toISOString(), end: endOfMonth.toISOString() }),
                getFacilityInfo(),
                getCalendarBlocks()
            ]);
            setCalendarReservations(resData || []);
            setClosures(closureData || []);
            setFacility(facilityData);
            setBlocks(blocksData || []);
        } catch (error) {
            setReservationError(error.message || `Could not load data.`);
        } finally {
            setIsLoadingReservations(false);
        }
    }, [calendarDisplayDate, currentUser]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchTableData = useCallback(async (silent = false) => {
        if (!currentUser?.id) return;
        if (!silent) setTableLoading(true);
        try {
            const data = await getMyReservations();
            setTableReservations(data || []);
        } catch (err) {
            setTableError(err.message || 'Failed to load reservations.');
        } finally {
            if (!silent) setTableLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchTableData();
    }, [fetchTableData]);

    // Refresh data when window gains focus (e.g. returning from DocuSign tab)
    useEffect(() => {
        const handleFocus = () => {
            console.log('[MyReservations] Window focused, refreshing data silently...');
            fetchTableData(true);
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchTableData]);

    const reservedDatesMap = useMemo(() => {
        const map = new Map();
        if (!currentUser?.id) return map;

        calendarReservations.forEach(res => {
            const startDate = res.startTime?.split('T')[0];
            const endDate = res.endTime?.split('T')[0];

            if (startDate && endDate) {
                const datesInRange = generateDateRange(startDate, endDate);
                datesInRange.forEach(dateStr => {
                    const isMyBooking = (res.userId === currentUser.id) || (currentUser.accountId && res.accountId === currentUser.accountId);
                    map.set(dateStr, {
                        id: res.id,
                        ...res,
                        type: isMyBooking ? res.status : 'otherReserved'
                    });
                });
            }
        });

        closures.forEach(c => {
            const start = c.startTime.split('T')[0];
            const end = c.endTime.split('T')[0];
            if (start && end) {
                generateDateRange(start, end).forEach(d => {
                    map.set(d, {
                        type: 'closure',
                        text: 'Unavailable',
                        reason: c.reason,
                        status: 'closed'
                    });
                });
            }
        });

        return map;
    }, [calendarReservations, closures, currentUser]);

    const handleDateSelect = (payload) => {
        setViewingReservation(null);
        setReservationError(null);
        setExpandedReservationId(null);
        setEditingReservationId(null);

        // 1. Check if payload is a block object
        if (typeof payload === 'object' && payload.startDate && payload.endDate) {
            const { startDate, endDate } = payload;
            // Toggle logic for the block
            if (selectedDates.length > 0 && selectedDates[0] === startDate && selectedDates[selectedDates.length - 1] === endDate) {
                setSelectedDates([]);
            } else {
                setSelectedDates(generateDateRange(startDate, endDate));
            }
            return;
        }

        // 2. Handle as free selection (string payload)
        // If in blocks mode and NOT an admin, ignore individual day clicks
        if (facility?.selectionMode === 'blocks' && currentUser.role !== 'Administrator') {
            return;
        }

        const clickedDateStr = payload;
        setSelectedDates(prevSelectedDates => {
            const sortedSelection = [...prevSelectedDates].sort();
            const selectionLength = sortedSelection.length;
            if (selectionLength === 0) return [clickedDateStr];
            if (selectionLength === 1) {
                const [start, end] = [sortedSelection[0], clickedDateStr].sort();
                if (start === end) return [];
                return generateDateRange(start, end);
            }
            return [clickedDateStr];
        });
    };

    const handleReservationSuccess = () => {
        setSelectedDates([]);
        fetchData();
        fetchTableData();
    };

    const handleShowDetails = (reservationDetails) => {
        setSelectedDates([]);
        if (viewingReservation?.id === reservationDetails.id && expandedReservationId === reservationDetails.id) {
            setViewingReservation(null);
            setExpandedReservationId(null);
        } else {
            setViewingReservation(reservationDetails);
            setExpandedReservationId(reservationDetails.id);
            setActiveFilterStatus('all');
            setTimeout(() => {
                const element = document.getElementById(`expanded-row-${reservationDetails.id}`);
                if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 10);
        }
    };

    const handleToggleExpand = (reservationId) => {
        const isCurrentlyExpanded = expandedReservationId === reservationId;
        if (isCurrentlyExpanded) {
            setExpandedReservationId(null);
            setViewingReservation(null);
            setEditingReservationId(null);
        } else {
            setExpandedReservationId(reservationId);
            setViewingReservation(tableReservations.find(r => r.id === reservationId) || null);
            setEditingReservationId(null);

            // Scroll to the expanded row
            setTimeout(() => {
                const element = document.getElementById(`expanded-row-${reservationId}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 10);
        }
    };

    const handleMonthChange = (newDate) => {
        setCalendarDisplayDate(newDate);
    };

    const filteredTableReservations = useMemo(() => {
        if (!tableReservations) return [];
        let filtered = tableReservations;
        if (activeFilterStatus === 'all') {
            filtered = tableReservations.filter(res => res.status === 'pending' || res.status === 'approved');
        } else if (activeFilterStatus === 'inactive') {
            filtered = tableReservations.filter(res => res.status === 'cancelled' || res.status === 'rejected');
        } else {
            filtered = tableReservations.filter(res => res.status === activeFilterStatus);
        }
        return filtered.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [tableReservations, activeFilterStatus]);

    const handlePerformTableCancel = async (id) => {
        if (!window.confirm('Cancel this reservation?')) return;
        try {
            await cancelReservation(id);
            fetchData();
            fetchTableData();
        } catch (err) {
            setReservationError('Failed to cancel.');
        }
    };

    const handleStartEdit = (res) => {
        setEditingReservationId(res.id);
        setEditableEventName(res.eventName || '');
        setEditableEventDescription(res.eventDescription || '');
        setEditableAdditionalInfo(res.additionalInfo || '');
        setEditableGuestName(res.guestName || '');
        setEditableGuestEmail(res.guestEmail || '');
        setEditableGuestPhone(res.guestPhone || '');
        setEditableGroupDescription(res.groupDescription || '');
        setEditablePurposeOfVisit(res.purposeOfVisit || '');
        setEditableAdultCount(res.adultCount || 0);
        setEditableChildCount(res.childCount || 0);
        setEditableResponsiblePerson(res.responsiblePerson || '');
        setEditError(null);
    };

    const handleSaveChanges = async (id) => {
        if (!editableGuestName.trim() || !editableResponsiblePerson.trim()) {
            setEditError('Contact Name and Responsible Person are required.');
            return;
        }

        // Auto-maintain Event Name/Description if not explicitly set
        let finalEventName = editableEventName.trim();
        if (!finalEventName) {
            finalEventName = editableGroupDescription ? `${editableGroupDescription} Reservation` : `Reservation for ${editableGuestName}`;
        }
        let finalDescription = editableEventDescription.trim();
        if (!finalDescription) {
            finalDescription = editablePurposeOfVisit || 'No description provided';
        }

        setIsSavingEdit(true);
        setEditError(null);
        try {
            await updateReservationDetails(id, {
                eventName: finalEventName,
                eventDescription: finalDescription,
                additionalInfo: editableAdditionalInfo.trim(),
                guestName: editableGuestName.trim(),
                guestEmail: editableGuestEmail.trim(),
                guestPhone: editableGuestPhone.trim(),
                groupDescription: editableGroupDescription.trim(),
                purposeOfVisit: editablePurposeOfVisit.trim(),
                adultCount: parseInt(editableAdultCount) || 0,
                childCount: parseInt(editableChildCount) || 0,
                responsiblePerson: editableResponsiblePerson.trim()
            });
            setEditingReservationId(null);
            fetchTableData();
            fetchData();
        } catch (err) {
            setEditError(err.response?.data?.message || err.message || 'Failed to save reservation details.');
        } finally {
            setIsSavingEdit(false);
        }
    };

    return (
        <div className={styles.reservationPageContainer}>
            <h2 className="page-main-title">My Reservations</h2>
            <p className={styles.pageInstructions}>
                {facility?.selectionMode === 'blocks' && currentUser.role !== 'Administrator'
                    ? 'Select a block on the calendar to start a reservation.'
                    : 'Select dates on the calendar to start a reservation.'}
            </p>

            {reservationError && <p className={styles.errorMessage}>{reservationError}</p>}

            <Calendar
                selectedDates={selectedDates}
                onDateSelect={handleDateSelect}
                reservedDatesMap={reservedDatesMap}
                onShowDetails={handleShowDetails}
                isLoading={isLoadingReservations}
                viewingReservation={viewingReservation}
                currentDisplayDate={calendarDisplayDate}
                onMonthChange={handleMonthChange}
                selectionMode={facility?.selectionMode || 'free'}
                blocks={blocks}
            />

            {selectedDates.length > 0 && !viewingReservation && (
                <div className={styles.newReservationFormSection}>
                    <ReservationForm
                        selectedDates={selectedDates}
                        onReservationSuccess={handleReservationSuccess}
                        onClearView={() => setSelectedDates([])}
                        currentUser={currentUser}
                        formTitle="New Reservation"
                        formContext="my-account"
                        expiryHours={facility?.pendingExpiryHours ?? 48}
                    />
                </div>
            )}

            <div className={styles.tabsContainer}>
                {USER_RESERVATION_STATUS_TABS.map(tab => (
                    <button
                        key={tab.status}
                        onClick={() => setActiveFilterStatus(tab.status)}
                        className={`${styles.tabButton} ${activeFilterStatus === tab.status ? styles.activeTab : ''}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className={styles.tableContainer}>
                {tableLoading ? (
                    <p>Loading reservations list...</p>
                ) : (
                    <div className={styles.tableContainer}>
                        {filteredTableReservations.length === 0 ? (
                            <p style={{ padding: '20px' }}>No reservations found for this filter.</p>
                        ) : (
                            <table className={styles.reservationTable}>
                                <thead>
                                    <tr>
                                        <th>Event Name</th>
                                        <th>Dates</th>
                                        <th className={styles.centerHeader}>Status</th>
                                        {activeFilterStatus !== 'inactive' && <th className={styles.centerHeader}>Action Required</th>}
                                        <th className={styles.centerHeader}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTableReservations.map((res) => (
                                        <React.Fragment key={`reservation-group-${res.id}`}>
                                            <tr
                                                id={`reservation-row-${res.id}`}
                                                className={`${styles.reservationRowClickable} ${expandedReservationId === res.id ? styles.activeExpandedRow : ''}`}
                                                onClick={() => handleToggleExpand(res.id)}
                                            >
                                                <td>{res.eventName || 'N/A'}</td>
                                                <td>{`${formatDate(res.startTime)} - ${formatDate(res.endTime)}`}</td>
                                                <td className={styles.centerCell}>
                                                    <span className={`${styles.status} ${styles[getStatusClass(res)] || styles.pending}`}>
                                                        {getStatusLabel(res)}
                                                    </span>
                                                </td>
                                                {activeFilterStatus !== 'inactive' && (
                                                    <td className={`${styles.actionRequiredCell} ${styles.centerCell}`} onClick={(e) => e.stopPropagation()}>
                                                        <ReservationProgressBar
                                                            reservation={res}
                                                            onSignAgreement={async () => {
                                                                setIsLoadingSigningUrl(res.id);
                                                                const signingWindow = window.open('', '_blank');
                                                                if (signingWindow) {
                                                                    signingWindow.opener = null;
                                                                    signingWindow.document.write('<!doctype html><title>Opening DocuSign</title><p>Opening DocuSign...</p>');
                                                                }
                                                                try {
                                                                    const url = await getSigningUrl(res.id);
                                                                    if (signingWindow) {
                                                                        signingWindow.location.href = url;
                                                                    } else {
                                                                        window.location.href = url;
                                                                    }
                                                                } catch (err) {
                                                                    if (signingWindow) signingWindow.close();
                                                                    setReservationError(err.message || 'Failed to get signing URL');
                                                                } finally {
                                                                    setIsLoadingSigningUrl(null);
                                                                }
                                                            }}
                                                            onMakePayment={() => navigate(`/account/payments?reservationId=${res.id}`)}
                                                            isLoadingSign={isLoadingSigningUrl === res.id}
                                                        />
                                                    </td>
                                                )}
                                                <td className={`${styles.actionButtonsCellContainer} ${styles.centerCell}`} onClick={(e) => e.stopPropagation()}>
                                                    <div className={styles.actionButtonsGroup}>
                                                        <Button
                                                            onClick={() => handleToggleExpand(res.id)}
                                                            variant="secondary"
                                                            size="sm"
                                                        >
                                                            View
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedReservationId === res.id && (
                                                <tr id={`expanded-row-${res.id}`} className={styles.expandedDetailRow}>
                                                    <td colSpan={5}>
                                                        {editingReservationId === res.id ? (
                                                            <div className={styles.editFormInTable} style={{ textAlign: 'left' }}>
                                                                <h4>Editing: {res.eventName}</h4>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>1. Name of Person Initiating Reservation</h5>
                                                                    <FormInput label="Full Name" type="text" value={editableGuestName} onChange={(e) => setEditableGuestName(e.target.value)} required />

                                                                    <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                                                                        <h6 style={{ margin: '0.5rem 0', color: '#666' }}>a) Contact Information</h6>
                                                                        <div style={{ display: 'flex', gap: '10px' }}>
                                                                            <FormInput label="Email" type="email" value={editableGuestEmail} onChange={(e) => setEditableGuestEmail(e.target.value)} containerStyle={{ flex: 1 }} required />
                                                                            <FormInput label="Phone" type="tel" value={editableGuestPhone} onChange={(e) => setEditableGuestPhone(e.target.value)} containerStyle={{ flex: 1 }} required />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>3. Description of Group</h5>
                                                                    <FormInput label="Group Description" type="text" value={editableGroupDescription} onChange={(e) => setEditableGroupDescription(e.target.value)} placeholder="i.e., College or District retreat; Service Group, etc." />

                                                                    <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                                                                        <label style={{ display: 'block', marginBottom: '5px', color: '#555' }}>Event Title (Calendar Display)</label>
                                                                        <input type="text" value={editableEventName} onChange={(e) => setEditableEventName(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                                    </div>
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>4. Purpose of Highview House Visit</h5>
                                                                    <FormInput label="Purpose of Visit" type="textarea" value={editablePurposeOfVisit} onChange={(e) => setEditablePurposeOfVisit(e.target.value)} rows={3} />
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>5. Approximate Number of Attendees</h5>
                                                                    <div style={{ display: 'flex', gap: '10px' }}>
                                                                        <FormInput label="Adults" type="number" value={editableAdultCount} onChange={(e) => setEditableAdultCount(e.target.value)} min="0" containerStyle={{ flex: 1 }} />
                                                                        <FormInput label="Children" type="number" value={editableChildCount} onChange={(e) => setEditableChildCount(e.target.value)} min="0" containerStyle={{ flex: 1 }} />
                                                                    </div>
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>6. Person Responsible for Payment Arrangements</h5>
                                                                    <FormInput label="Responsible Person" type="text" value={editableResponsiblePerson} onChange={(e) => setEditableResponsiblePerson(e.target.value)} required />
                                                                </div>

                                                                <FormInput
                                                                    label="Additional Info"
                                                                    type="textarea"
                                                                    value={editableAdditionalInfo}
                                                                    onChange={(e) => setEditableAdditionalInfo(e.target.value)}
                                                                />

                                                                {editError && <p className={styles.errorMessage}>{editError}</p>}
                                                                <div className={styles.editActions}>
                                                                    <Button
                                                                        onClick={(e) => { e.stopPropagation(); handleSaveChanges(res.id); }}
                                                                        variant="success"
                                                                        size="sm"
                                                                        disabled={isSavingEdit || !editableEventName.trim() || !editableGuestName.trim() || !editableResponsiblePerson.trim()}
                                                                    >
                                                                        {isSavingEdit ? 'Saving...' : 'Save Changes'}
                                                                    </Button>
                                                                    <Button
                                                                        onClick={(e) => { e.stopPropagation(); setEditingReservationId(null); }}
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        disabled={isSavingEdit}
                                                                    >
                                                                        Cancel Edit
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={styles.readOnlyDetailsContainer}>
                                                                <p><strong>Event Name:</strong> {res.eventName || 'N/A'}</p>
                                                                {res.eventDescription && <p><strong>Event Description:</strong> {res.eventDescription}</p>}

                                                                <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                                                                    <p style={{ margin: '0.25rem 0' }}><strong>Guest:</strong> {res.guestName} &lt;{res.guestEmail}&gt; {res.guestPhone ? `| Phone: ${res.guestPhone}` : ''}</p>
                                                                    {res.groupDescription && <p style={{ margin: '0.25rem 0' }}><strong>Group:</strong> {res.groupDescription}</p>}
                                                                    {res.purposeOfVisit && <p style={{ margin: '0.25rem 0' }}><strong>Purpose:</strong> {res.purposeOfVisit}</p>}
                                                                    {(res.adultCount > 0 || res.childCount > 0) && <p style={{ margin: '0.25rem 0' }}><strong>Attendees:</strong> {res.adultCount} Adults, {res.childCount} Children</p>}
                                                                    {res.responsiblePerson && <p style={{ margin: '0.25rem 0' }}><strong>Responsible Person:</strong> {res.responsiblePerson}</p>}
                                                                </div>

                                                                {res.additionalInfo && <p><strong>Additional Info:</strong> {res.additionalInfo}</p>}
                                                                {res.adminNotes && <p><strong>Admin Notes:</strong> {res.adminNotes}</p>}
                                                                {res.cancellationReason && <p><strong>Cancellation Reason:</strong> {res.cancellationReason}</p>}

                                                                <p><strong>Dates:</strong> {formatDate(res.startTime)} - {formatDate(res.endTime)}</p>
                                                                <p><strong>Current Status:</strong> <span className={`${styles.status} ${styles[getStatusClass(res)] || styles.pending}`}>{getStatusLabel(res)}</span></p>

                                                                <div className={styles.expandedActions} style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                                                    {!['cancelled', 'rejected'].includes(res.status) && res.cancellationReason !== 'expired_unpaid' && (
                                                                        <Button
                                                                            onClick={(e) => { e.stopPropagation(); handleStartEdit(res); }}
                                                                            variant="outlinePrimary"
                                                                            size="sm"
                                                                            className={styles.expandedActionButton}
                                                                        >
                                                                            Edit Reservation
                                                                        </Button>
                                                                    )}
                                                                    {CANCELLABLE_STATUSES.includes(res.status) && (
                                                                        <Button
                                                                            onClick={(e) => { e.stopPropagation(); handlePerformTableCancel(res.id); }}
                                                                            variant="outlineDanger"
                                                                            size="sm"
                                                                            className={styles.expandedActionButton}
                                                                        >
                                                                            Cancel Reservation
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyReservationsPage;
